/**
 * LOGOI LAB - useBayesianWeights Hook
 * Constrained Bayesian Weight Adaptation (CBWA) Engine
 * 
 * Learns optimal weight configurations from session history
 * using Dirichlet-prior Bayesian updates with Logos-positive constraints.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { useState, useCallback, useMemo } from 'react';
import { DEFAULT_WEIGHTS } from './useCustomWeights';
import type { WeightMetric } from './useCustomWeights';

// ═══ CBWA PARAMETERS ═══
const N0 = 50;           // Pseudo-count: theory worth 50 sessions of evidence
const ETA = 0.1;         // Learning rate (conservative)
const BETA_MAX = 0.3;    // Max smoothing factor per iteration
const T_MIN = 10;        // Minimum sessions before learning
const FAITH_MIN = 0.10;  // Theological minimum for faith weight
const LOGOS_POS_THRESHOLD = 0.95; // Λ can't drop more than 5%

export interface SessionData {
  lambda: number;
  omega?: number;
  V?: number;
  S?: number;
  C?: number;
  F?: number;
  Q?: number;
  state_snapshot?: Record<string, number> | null;
  domain_scores?: Record<string, number> | null;
}

export interface CBWAResult {
  learnedWeights: {
    lambda: Record<string, number>;
    viability: Record<string, number>;
    entropy: Record<string, number>;
  };
  confidence: number;       // 0-1: how confident we are in learned weights
  sessionsUsed: number;
  convergenceStatus: 'insufficient' | 'learning' | 'converging' | 'converged';
  attributions: Record<string, number>; // per-dimension contribution scores
  deltaFromTheory: number;  // total distance from theoretical weights
}

/**
 * Extract sequential ΔΛ and attribute to dimensions.
 * contribution(i, t) = x_i(t) × ΔΛ(t) / Σⱼ x_j(t)
 */
function computeAttributions(
  sessions: SessionData[],
  dimKeys: string[]
): Record<string, number> {
  const attr: Record<string, number> = {};
  for (const k of dimKeys) attr[k] = 0;

  for (let t = 1; t < sessions.length; t++) {
    const prev = sessions[t - 1];
    const curr = sessions[t];
    if (!curr.state_snapshot || !prev.state_snapshot) continue;

    const dLambda = (curr.lambda ?? 0) - (prev.lambda ?? 0);
    if (Math.abs(dLambda) < 0.001) continue; // Skip negligible changes

    // Sum of all dimension values for normalization
    let sumX = 0;
    for (const k of dimKeys) {
      sumX += (curr.state_snapshot[k] ?? 0.5);
    }
    if (sumX < 0.01) continue;

    for (const k of dimKeys) {
      const xi = curr.state_snapshot[k] ?? 0.5;
      // Positive ΔΛ credits high dims; negative ΔΛ penalizes low dims
      attr[k] += xi * dLambda / sumX;
    }
  }

  return attr;
}

/**
 * Bayesian update: α_i = w_theory(i) × N₀ + η × Σ max(0, contribution(i))
 * Then normalize.
 */
function bayesianUpdate(
  defaults: Record<string, number>,
  attributions: Record<string, number>,
  sessionsCount: number
): Record<string, number> {
  const keys = Object.keys(defaults);
  const alpha: Record<string, number> = {};

  // Prior from theory
  for (const k of keys) {
    alpha[k] = (defaults[k] ?? 0) * N0;
  }

  // Add positive attributions
  for (const k of keys) {
    alpha[k] += ETA * Math.max(0, attributions[k] ?? 0) * sessionsCount;
  }

  // Normalize
  const sum = Object.values(alpha).reduce((a, b) => a + b, 0);
  const result: Record<string, number> = {};
  for (const k of keys) {
    result[k] = sum > 0 ? alpha[k] / sum : defaults[k];
  }

  return result;
}

/**
 * Apply constraints: clamp to [50% theory, 200% theory], faith ≥ 0.10
 * 
 * Justification (Dirichlet posterior ±2σ credible interval):
 *   For α_i = w_i × N₀ with N₀=50, σ_i ≈ √(α_i(α₀-α_i)/(α₀²(α₀+1)))
 *   ±2σ corresponds to approximately [50%, 200%] of the theoretical value.
 *   This is consistent with:
 *   - Bayesian 95% credible intervals (Murphy, 2012)
 *   - FDA adaptive trial bounds (FDA Guidance, 2019)
 *   - Kalman filter gain stability (Friston et al., 2008)
 *   - Psychometric factor loading variation (Costa & McCrae, 1992)
 */
function applyConstraints(
  weights: Record<string, number>,
  defaults: Record<string, number>
): Record<string, number> {
  const constrained: Record<string, number> = {};

  for (const [k, w] of Object.entries(weights)) {
    const theory = defaults[k] ?? 0.01;
    const wMin = Math.max(0.005, theory * 0.50);
    const wMax = Math.min(0.30, theory * 2.0);
    constrained[k] = Math.max(wMin, Math.min(wMax, w));
  }

  // Faith minimum (theological non-negotiable)
  if (constrained.faith !== undefined && constrained.faith < FAITH_MIN) {
    constrained.faith = FAITH_MIN;
  }

  // Re-normalize
  const sum = Object.values(constrained).reduce((a, b) => a + b, 0);
  if (sum > 0 && Math.abs(sum - 1.0) > 0.001) {
    for (const k of Object.keys(constrained)) {
      constrained[k] /= sum;
    }
    // Re-enforce faith minimum after normalization
    if (constrained.faith !== undefined && constrained.faith < FAITH_MIN) {
      constrained.faith = FAITH_MIN;
    }
  }

  return constrained;
}

/**
 * Temporal smoothing: w_final = β × w_candidate + (1-β) × w_current
 */
function temporalSmoothing(
  candidate: Record<string, number>,
  current: Record<string, number>,
  sessionsCount: number
): Record<string, number> {
  const beta = Math.min(BETA_MAX, sessionsCount / 100);
  const result: Record<string, number> = {};

  for (const k of Object.keys(candidate)) {
    result[k] = beta * (candidate[k] ?? 0) + (1 - beta) * (current[k] ?? candidate[k] ?? 0);
  }

  return result;
}

/**
 * Run the full CBWA pipeline for one metric.
 */
function runCBWA(
  sessions: SessionData[],
  defaults: Record<string, number>,
  currentWeights: Record<string, number>
): { weights: Record<string, number>; attributions: Record<string, number> } {
  const dimKeys = Object.keys(defaults);

  // Step 1: Attribution
  const attributions = computeAttributions(sessions, dimKeys);

  // Step 2: Bayesian update
  const candidate = bayesianUpdate(defaults, attributions, sessions.length);

  // Step 3: Constraints
  const constrained = applyConstraints(candidate, defaults);

  // Step 4: Temporal smoothing
  const smoothed = temporalSmoothing(constrained, currentWeights, sessions.length);

  return { weights: smoothed, attributions };
}

/**
 * Compute confidence score based on session count and attribution stability.
 */
function computeConfidence(sessionsCount: number, attributions: Record<string, number>): number {
  if (sessionsCount < T_MIN) return 0;

  // Session-based confidence (logistic curve, 50% at N₀ sessions)
  const sessionConf = 1 / (1 + Math.exp(-0.1 * (sessionsCount - N0)));

  // Attribution stability (lower variance = higher confidence)
  const attrValues = Object.values(attributions);
  const mean = attrValues.reduce((a, b) => a + b, 0) / attrValues.length;
  const variance = attrValues.reduce((a, v) => a + (v - mean) ** 2, 0) / attrValues.length;
  const stabilityConf = 1 / (1 + variance * 100);

  return Math.min(1, sessionConf * 0.7 + stabilityConf * 0.3);
}

/**
 * Determine convergence status.
 */
function getConvergenceStatus(
  sessionsCount: number,
  deltaFromTheory: number
): CBWAResult['convergenceStatus'] {
  if (sessionsCount < T_MIN) return 'insufficient';
  if (sessionsCount < N0 / 2) return 'learning';
  if (deltaFromTheory < 0.05) return 'converged';
  return 'converging';
}

/**
 * Main CBWA computation function.
 * Takes session history and returns learned weights with metadata.
 */
export function computeCBWA(
  sessions: SessionData[],
  currentLambdaWeights: Record<string, number>,
  currentViabilityWeights: Record<string, number>,
  currentEntropyWeights: Record<string, number>
): CBWAResult {
  const validSessions = sessions.filter(s => s.state_snapshot && s.lambda != null);

  if (validSessions.length < T_MIN) {
    return {
      learnedWeights: {
        lambda: { ...DEFAULT_WEIGHTS.lambda },
        viability: { ...DEFAULT_WEIGHTS.viability },
        entropy: { ...DEFAULT_WEIGHTS.entropy },
      },
      confidence: 0,
      sessionsUsed: validSessions.length,
      convergenceStatus: 'insufficient',
      attributions: {},
      deltaFromTheory: 0,
    };
  }

  // Run CBWA for each metric
  const lambdaResult = runCBWA(validSessions, DEFAULT_WEIGHTS.lambda, currentLambdaWeights);
  const viabilityResult = runCBWA(validSessions, DEFAULT_WEIGHTS.viability, currentViabilityWeights);
  const entropyResult = runCBWA(validSessions, DEFAULT_WEIGHTS.entropy, currentEntropyWeights);

  // Compute total distance from theory
  let totalDelta = 0;
  for (const k of Object.keys(DEFAULT_WEIGHTS.lambda)) {
    totalDelta += Math.abs((lambdaResult.weights[k] ?? 0) - (DEFAULT_WEIGHTS.lambda[k] ?? 0));
  }

  const confidence = computeConfidence(validSessions.length, lambdaResult.attributions);
  const convergenceStatus = getConvergenceStatus(validSessions.length, totalDelta);

  return {
    learnedWeights: {
      lambda: lambdaResult.weights,
      viability: viabilityResult.weights,
      entropy: entropyResult.weights,
    },
    confidence,
    sessionsUsed: validSessions.length,
    convergenceStatus,
    attributions: lambdaResult.attributions,
    deltaFromTheory: totalDelta,
  };
}

/**
 * Hook for Bayesian weight learning.
 * Fetches session history from DB and computes CBWA.
 */
export function useBayesianWeights() {
  const [cbwaResult, setCbwaResult] = useState<CBWAResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runLearning = useCallback(async (
    currentLambdaWeights: Record<string, number>,
    currentViabilityWeights: Record<string, number>,
    currentEntropyWeights: Record<string, number>
  ) => {
    setLoading(true);
    setError(null);

    try {
      const userId = sessionStorage.getItem('logos-db-user-id');
      if (!userId) {
        setError('No user ID found');
        setLoading(false);
        return null;
      }

      // Fetch sessions with state_snapshot from DB
      const res = await fetch(`/api/sessions?user_id=${userId}&limit=200`);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();

      const sessions: SessionData[] = (data.sessions || [])
        .reverse() // API returns DESC, we need ASC
        .map((s: any) => ({
          lambda: parseFloat(s.lambda),
          omega: parseFloat(s.omega),
          V: parseFloat(s.viability),
          S: parseFloat(s.entropy),
          C: parseFloat(s.coherence),
          F: parseFloat(s.free_energy),
          Q: parseFloat(s.trajectory),
          state_snapshot: typeof s.state_snapshot === 'string'
            ? JSON.parse(s.state_snapshot)
            : s.state_snapshot,
          domain_scores: typeof s.domain_scores === 'string'
            ? JSON.parse(s.domain_scores)
            : s.domain_scores,
        }));

      const result = computeCBWA(
        sessions,
        currentLambdaWeights,
        currentViabilityWeights,
        currentEntropyWeights
      );

      setCbwaResult(result);
      setLoading(false);
      return result;
    } catch (e: any) {
      console.error('[CBWA] Learning error:', e.message);
      setError(e.message);
      setLoading(false);
      return null;
    }
  }, []);

  return {
    cbwaResult,
    loading,
    error,
    runLearning,
  };
}
